import * as React from 'react';
import { cn } from '@/lib/utils';
import { Combobox, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList, ComboboxPopup } from '@/components/ui/combobox';

/**
 * Componente Combobox de seleção única.
 *
 * @param {Object} props
 * @param {Array} props.options - Lista de objetos para as opções.
 * @param {string} [props.labelKey='label'] - Chave do objeto para exibição do texto.
 * @param {string} [props.valueKey='value'] - Chave do objeto para o valor.
 * @param {string} [props.placeholder='Selecione...'] - Texto do placeholder.
 * @param {boolean} [props.loading=false] - Indica se as opções estão sendo carregadas.
 * @param {string} [props.emptyMessage='Nenhum resultado encontrado.'] - Mensagem quando não houver resultados.
 */
export const ComboboxSingle = React.forwardRef(
  (
    {
      className,
      options = [],
      labelKey = 'label',
      valueKey = 'value',
      placeholder = 'Selecione...',
      loading = false,
      emptyMessage = 'Nenhum resultado encontrado.',
      value, // 👈 agora pode ser string
      onValueChange, // 👈 devolve string
      disabled = false,
      ...props
    },
    ref
  ) => {
    // 🔁 transforma string -> objeto
    const selectedItem = React.useMemo(() => {
      if (value === null || value === undefined) return null;

      // se já for objeto, mantém compatibilidade
      if (typeof value === 'object') return value;

      return options.find(opt => opt?.[valueKey] === value) ?? null;
    }, [value, options, valueKey]);

    // 🔁 transforma objeto -> string
    const handleChange = React.useCallback(
      item => {
        if (!onValueChange) return;

        if (!item) {
          onValueChange(null);
          return;
        }

        onValueChange(item?.[valueKey]);
      },
      [onValueChange, valueKey]
    );

    // Helper para converter o item em texto
    const getItemText = React.useCallback(
      item => {
        if (!item) return '';
        if (typeof item === 'string') return item;
        return item?.[labelKey] ?? '';
      },
      [labelKey]
    );

    return (
      <Combobox
        ref={ref}
        items={options}
        className={cn('w-full', className)}
        getItemText={getItemText}
        value={selectedItem} // 👈 objeto real
        onValueChange={handleChange} // 👈 devolve string
        disabled={disabled}
        {...props}
      >
        <ComboboxInput
          placeholder={loading ? 'Carregando...' : placeholder}
          getItemText={getItemText}
        />
        <ComboboxPopup>
          <ComboboxEmpty>{loading ? 'Carregando opções...' : emptyMessage}</ComboboxEmpty>
          <ComboboxList>
            {item => (
              <ComboboxItem
                key={item[valueKey]}
                value={item}
              >
                {getItemText(item)}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxPopup>
      </Combobox>
    );
  }
);

ComboboxSingle.displayName = 'ComboboxSingle';
